/**
 * Hook: una consulta por estado y conteos agrupados por transportadora
 */
import { useCallback, useEffect, useState } from 'react'
import { MANIFIESTOS_CARRIERS } from '../../config/manifiestos/manifiestosCarriersConfig'
import { getManifiestoOrdersByStatus } from '../../services/manifiestos/manifiestosService'
import type { ManifiestoCarrierCount } from '../../types/manifiestos'
import { countOrdersByCarrier } from '../../utils/manifiestos/carrierMatch'

function buildLoadingCounts(): ManifiestoCarrierCount[] {
  return MANIFIESTOS_CARRIERS.map((carrier) => ({
    carrierId: carrier.id,
    count: 0,
    isLoading: true,
    error: null,
  }))
}

function buildCountsFromMap(
  map: Record<string, number>,
  error: string | null = null
): ManifiestoCarrierCount[] {
  return MANIFIESTOS_CARRIERS.map((carrier) => ({
    carrierId: carrier.id,
    count: map[carrier.id] ?? 0,
    isLoading: false,
    error,
  }))
}

export function useManifiestosCounts() {
  const [counts, setCounts] = useState<ManifiestoCarrierCount[]>(
    buildLoadingCounts
  )
  const [orders, setOrders] = useState<unknown[]>([])
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)
  const [globalError, setGlobalError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setIsRefreshing(true)
    setCounts(buildLoadingCounts())
    setGlobalError(null)

    try {
      const allOrders = await getManifiestoOrdersByStatus()
      const map = countOrdersByCarrier(allOrders)
      setOrders(allOrders)
      setCounts(buildCountsFromMap(map))
      setLastUpdatedAt(new Date())
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Error al cargar pedidos listos para despachar'
      setOrders([])
      setGlobalError(message)
      setCounts(
        MANIFIESTOS_CARRIERS.map((carrier) => ({
          carrierId: carrier.id,
          count: 0,
          isLoading: false,
          error: message,
        }))
      )
    } finally {
      setIsRefreshing(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return {
    carriers: MANIFIESTOS_CARRIERS,
    counts,
    orders,
    isRefreshing,
    lastUpdatedAt,
    globalError,
    refresh,
  }
}
